/**
 * COPYING TEXT, decided in one place. Four call sites had four different answers.
 *
 * `navigator.clipboard.writeText` is ABSENT over plain HTTP — not failing, absent — so
 * the whole API is `undefined` outside a secure context. This app is reached over plain
 * HTTP regularly (a dev server on `http://<ip>:5180`, and any machine addressed by IP),
 * which is exactly when someone most wants to copy an install command out of it.
 *
 * What the four did:
 *
 *   FileBrowser      guard + try/catch + a fallback that SHOWS the text to copy by hand
 *   InstallSection   guard + fallback, but no catch — a REJECTED write (the permission
 *                    exists and the user denied it, or the document is not focused)
 *                    became an unhandled rejection and the button just sat there
 *   TerminalView     guard, no fallback — over HTTP a tmux selection went nowhere and
 *                    nothing said so, which reads as "the copy feature is broken"
 *   AgentSheet       `void navigator.clipboard?.writeText(text)` — NO GUARD AT ALL. The
 *                    `?.` makes it silently evaluate to undefined over HTTP, so the
 *                    button was inert and could not even fail. Copying a README or an
 *                    agent card simply did nothing, with no way to tell.
 *
 * WHAT IS SHARED AND WHAT IS NOT. The DECISION is shared: is the API there, did the
 * write actually go through, and if not, why. The PRESENTATION is deliberately left to
 * each caller — a status line, a text selection the reader can drag over, a line written
 * into the terminal itself. Those differ because the surfaces differ, and forcing one
 * notice shape onto all four would be a worse answer than the four they had.
 *
 * So this returns a result rather than showing anything, and it never throws: a copy
 * button that raises into the page is strictly worse than one that reports it failed.
 */

export type CopyResult =
  | { ok: true }
  /** No clipboard API here at all — plain HTTP. Nothing to retry; offer the text. */
  | { ok: false; reason: 'insecure' }
  /** The API exists and said no: permission denied, or the document is not focused. */
  | { ok: false; reason: 'refused' }

export async function copyText(text: string): Promise<CopyResult> {
  if (!window.isSecureContext || !navigator.clipboard?.writeText) {
    return { ok: false, reason: 'insecure' }
  }
  try {
    await navigator.clipboard.writeText(text)
    return { ok: true }
  } catch {
    // Rejections are ordinary here, not exceptional: Safari refuses a write that is not
    // inside a user gesture, and every browser refuses one from an unfocused document.
    return { ok: false, reason: 'refused' }
  }
}
